import React, { useState } from 'react';
import { observer } from 'mobx-react';
import {
  IconType, notification, NotificationType, ButtonProps, Icon,
} from '@routific/hive';
import { ReoptimizeIcon } from '@routific/hive/v5';
import type { V5ButtonHierarchy } from '@routific/hive/v5';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../../store/mst';
import { RouteProps } from '../../../../store/mst/utils/getRoutesProps';
import ReoptimizeConfirmModal from '../../../scheduling/ReoptimizeConfirmModal';
import ActionButton, { CommonActionTypes, ButtonTypes } from '../../../shared/ActionButton';

interface Props {
  routeProps: RouteProps;
  type?: ButtonTypes;
  buttonProps?: ButtonProps;
  // Render as a v5 Button, for the v5 chrome (the page footer's action band).
  v5?: boolean;
  v5Hierarchy?: V5ButtonHierarchy;
}

const ReoptimizeButton: React.FC<Props> = ({
  routeProps, type, buttonProps,
  v5,
  v5Hierarchy,
}) => {
  const store = useStore();
  const selectedWorkspaceId = store.ui.selectedWorkspace?.id;

  const {
    uuids, isStarted, isDispatched, isLocked, isLoading, isReversing, disabled, noOrders,
  } = routeProps;

  const { t } = useTranslation('components');
  const [showReoptimizeModal, setShowReoptimizeModal] = useState<boolean>(false);

  const startReoptimize = async () => {
    if (selectedWorkspaceId) {
      try {
        await store.model.optimizeRoutes(uuids);

        setShowReoptimizeModal(false);

        if (store.ui.selectedRoutesBulk.size > 0) {
          store.ui.clearSelectedRoutesBulk();
        }
      } catch (error) {
        notification({
          type: NotificationType.ERROR,
          id: `${selectedWorkspaceId}-reoptimize-failed`,
          // @ts-ignore - response is not typed
          message: error?.response?.errors[0].message,
          dataCy: 'reoptimize-failure-notification',
        });
      }
      store.mapView.exitDrawMode();
    }
  };

  const handleClickForModal = async () => {
    if (store.ui.disableConfirmReoptimizeModal) {
      await startReoptimize();
    } else {
      setShowReoptimizeModal(true);
    }
  };

  const getTooltipText = () => {
    if (disabled || noOrders) {
      if (isStarted) {
        return t('publish.tooltip.reoptimizeStarted');
      }
      if (isLocked || isLoading || isReversing) {
        return t('publish.tooltip.routeLocked');
      }
      if (noOrders) {
        return t('moreActionsButton.route.button.reoptimize.tooltip.noOrders');
      }
      // CREATE_OPTIMIZATION still closes on published routes (fresh event uuids); check after more specific reasons.
      if (isDispatched) {
        return t('publish.tooltip.reoptimizePublished');
      }
      return t('moreActionsDropdown.tooltip.noPermission');
    }
    if (type === ButtonTypes.BUTTON) {
      return t('moreActionsButton.route.button.reoptimize.tooltip.button');
    }
    return undefined;
  };

  return (
    <>
      <ReoptimizeConfirmModal
        visible={showReoptimizeModal}
        onReoptimize={startReoptimize}
        onCancel={() => setShowReoptimizeModal(false)}
        numRoutes={uuids.length}
        reoptimizeButton
      />
      <ActionButton
        v5={v5}
        v5Hierarchy={v5Hierarchy}
        v5Icon={<ReoptimizeIcon size="md" />}
        name={CommonActionTypes.REOPTIMIZE}
        tooltipProps={{
          title: getTooltipText(),
        }}
        type={type}
        disabled={disabled || noOrders}
        menuItemText={t('moreActionsButton.route.button.reoptimize.text')}
        menuItemProps={{
          onClick: handleClickForModal,
          icon: <Icon icon={IconType.Reoptimize} />,
        }}
        buttonProps={{
          icon: IconType.Reoptimize,
          style: { pointerEvents: isStarted ? 'none' : 'auto' },
          onClick: handleClickForModal,
          ...buttonProps,
        }}
      />
    </>
  );
};

export default observer(ReoptimizeButton);
